import React, { useState, useMemo, useEffect } from 'react'

import { AutocompleteInputChangeReason, AutocompleteChangeReason } from '@mui/material'
import { useDebounce } from 'use-debounce'

import { Address } from '../../../ana-toolbox'
import { googlePlacesHelpers } from '../helpers/googlePlaces'
import createPlacesService from '../helpers/places'

type UseAddressSuggestionsParams = {
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void
  onReceiveAddress?: (address: Address) => void
  name: string
  value?: string
  containerElement: HTMLDivElement | null
}

export const useAddressSuggestions = ({
  onChange,
  onReceiveAddress,
  name,
  value: externalValue,
  containerElement,
}: UseAddressSuggestionsParams) => {
  const [internalValue, setInternalValue] = useState('')
  const [options, setOptions] = useState<google.maps.places.AutocompletePrediction[]>([])
  const value = externalValue ?? internalValue
  const [debouncedValue] = useDebounce(value, 200)

  const placesService = useMemo(() => {
    if (containerElement) {
      return createPlacesService(containerElement)
    }
  }, [containerElement])

  // This function will decide wich event handler to use. If the `onChange` is passed
  // as prop, it means that the component is controlled, so, we need to call the `onChange`
  // prop, if not, we use the internal handler (`setInternalValue).
  const dispatchChange = (v: string) => {
    if (onChange) {
      onChange({
        currentTarget: {
          name: name || '',
          value: v,
        },
      } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>)
    } else {
      setInternalValue(v)
    }
  }

  const handleInputChange = (_: React.ChangeEvent<unknown>, v: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'input') {
      dispatchChange(v)
    }
  }

  const handlePlacePredictions = setOptions

  const handleChange = (
    _: React.SyntheticEvent,
    option: string | google.maps.places.AutocompletePrediction | undefined,
    reason: AutocompleteChangeReason
  ): void => {
    const isAutocompletePrediction = typeof option === 'object'

    if (reason === 'selectOption' && isAutocompletePrediction) {
      const autocompletePrediction = option as google.maps.places.AutocompletePrediction

      placesService
        ?.getPlaceDetails({ placeId: autocompletePrediction.place_id, fields: ['address_components'] })
        .then((data) => {
          if (!data) return

          const address = googlePlacesHelpers.parsePlaceDetails(data)
          const value = [address.street, address.number].filter(Boolean).join(' ')

          /* Only propagate the change if the value is truthy, otherwise, we preserve the user input.
           * This is needed because there are cases where the `street` and `number` are not
           * present in the Google places API.
           *
           * Searches like "Berlin" (city name), "Germany" (country) don't return street and number.
           * and if we update the input, we will make it empty.
           */
          if (value) dispatchChange(value)

          if (onReceiveAddress) onReceiveAddress(address)
        })
        .catch(() => {})
    }
  }

  useEffect(() => {
    if (debouncedValue) {
      placesService
        ?.getPlacePredictions({ input: debouncedValue })
        .then(setOptions)
        .catch(() => {})
    }
  }, [placesService, debouncedValue, handlePlacePredictions])

  return {
    value,
    options,
    handleChange,
    handleInputChange,
  }
}
